import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

/**
 * Compact sparkline of the user's daily leaderboard rank over the last N days.
 *
 * Renders nothing when the user has never been ranked (no bot sells at all)
 * so we don't clutter the dashboard for first-time users. Uses a reversed
 * Y-axis so a rank climb visually points up.
 */
export default function RankTrendChart({ days = 14 }) {
  const [series, setSeries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get(`/me/rank/history?days=${days}`)
      .then((r) => setSeries(r.data.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoaded(true));
  }, [days]);

  // Only show when we have at least two ranked days — otherwise a sparkline
  // of one point looks broken.
  const ranked = series.filter((s) => s.rank != null);
  if (loaded && ranked.length < 2) return null;
  if (!loaded) return null;

  // Recharts renders `null` gaps for missing ranks — no manual bridging needed.
  const first = ranked[0]?.rank;
  const last = ranked[ranked.length - 1]?.rank;
  const delta = first != null && last != null ? first - last : 0; // positive = climbed
  const trendUp = delta > 0;
  const trendDown = delta < 0;
  const trendColor = trendUp ? "#00C805" : trendDown ? "#FF3B30" : "#71717a";
  const TrendIcon = trendUp ? TrendUp : trendDown ? TrendDown : Minus;

  return (
    <div
      className="p-4 border border-zinc-800 rounded-sm bg-zinc-900/40"
      data-testid="rank-trend-chart"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Leaderboard rank · {days}d
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <div className="mono text-2xl font-bold text-white" data-testid="rank-current">
              #{last}
            </div>
            <div
              className="mono text-[10px] uppercase tracking-widest flex items-center gap-1"
              style={{ color: trendColor }}
              data-testid="rank-delta"
            >
              <TrendIcon size={11} weight="bold" />
              {delta > 0 && `↑ ${delta}`}
              {delta < 0 && `↓ ${Math.abs(delta)}`}
              {delta === 0 && "flat"}
            </div>
          </div>
        </div>
      </div>
      <div className="h-14 mt-2 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series}>
            {/* Reversed axis: rank 1 at top, so a climb goes visually upward */}
            <YAxis reversed hide domain={["dataMin", "dataMax"]} />
            <Tooltip
              contentStyle={{
                background: "#18181b",
                border: "1px solid #27272a",
                borderRadius: 2,
                fontFamily: "JetBrains Mono",
                fontSize: 11,
              }}
              labelStyle={{ color: "#a1a1aa" }}
              formatter={(v) => (v == null ? "—" : `#${v}`)}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ""}
            />
            <Line
              type="monotone"
              dataKey="rank"
              stroke={trendColor}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
